<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['use', 'edit', 'duplicate', 'delete', 'preview']);

const messageTypeIcons = {
  text: 'i-lucide-message-square',
  image: 'i-lucide-image',
  video: 'i-lucide-video',
  audio: 'i-lucide-music',
  document: 'i-lucide-file-text',
};

const categoryColors = {
  welcome: 'blue',
  follow_up: 'purple',
  promotion: 'green',
  reminder: 'yellow',
  notification: 'gray',
};

const truncateContent = (content, maxLength = 150) => {
  if (!content || content.length <= maxLength) return content;
  return content.substring(0, maxLength) + '...';
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const formatVariable = variable => {
  return `{{${variable}}}`;
};
</script>

<template>
  <div
    class="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
  >
    <div class="flex items-start justify-between">
      <div class="flex items-start gap-3">
        <div
          class="flex h-10 w-10 items-center justify-center rounded-lg"
          :class="[
            template.is_ai_template
              ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
          ]"
        >
          <span
            :class="
              template.is_ai_template
                ? 'i-lucide-sparkles'
                : messageTypeIcons[template.message_type]
            "
            class="h-5 w-5"
          />
        </div>
        <div>
          <div class="flex items-center gap-2">
            <h3 class="font-medium text-slate-900 dark:text-slate-100">
              {{ template.name }}
            </h3>
            <span
              v-if="template.category"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-500/10 text-slate-500"
            >
              {{ template.category }}
            </span>
            <span
              v-if="template.is_ai_template"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/10 text-purple-500"
            >
              IA
            </span>
          </div>
          <p
            v-if="template.description"
            class="mt-1 text-sm text-slate-500 dark:text-slate-400"
          >
            {{ template.description }}
          </p>
        </div>
      </div>
      <span
        v-if="!template.active"
        class="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-500/10 text-slate-500"
      >
        Inativo
      </span>
    </div>

    <div class="mt-4 rounded-lg bg-slate-50 p-3 dark:bg-slate-900">
      <p class="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
        {{ truncateContent(template.content) }}
      </p>
    </div>

    <div v-if="template.variables?.length" class="mt-3">
      <p class="text-xs text-slate-500 dark:text-slate-400">
        Variáveis:
        <span
          v-for="(variable, index) in template.variables"
          :key="variable"
          class="ml-1"
        >
          <code class="rounded bg-slate-200 px-1 text-xs dark:bg-slate-700">
            {{ formatVariable(variable) }}
          </code>
          <span v-if="index < template.variables.length - 1">, </span>
        </span>
      </p>
    </div>

    <div
      class="mt-4 flex items-center justify-between border-t border-slate-100 pt-4 dark:border-slate-700"
    >
      <div
        class="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400"
      >
        <span class="flex items-center gap-1">
          <span class="i-lucide-bar-chart-2 h-3 w-3" />
          {{ template.usage_count || 0 }} usos
        </span>
        <span v-if="template.last_used_at" class="flex items-center gap-1">
          <span class="i-lucide-clock h-3 w-3" />
          Último uso: {{ formatDate(template.last_used_at) }}
        </span>
      </div>

      <div class="flex gap-2">
        <Button
          size="small"
          variant="ghost"
          color="red"
          icon="i-lucide-trash-2"
          @click="emit('delete', template)"
        />
        <Button
          size="small"
          variant="ghost"
          icon="i-lucide-copy"
          @click="emit('duplicate', template)"
        />
        <Button
          size="small"
          variant="ghost"
          icon="i-lucide-eye"
          @click="emit('preview', template)"
        />
        <Button
          size="small"
          variant="outline"
          icon="i-lucide-pencil"
          @click="emit('edit', template)"
        >
          Editar
        </Button>
        <Button
          size="small"
          icon="i-lucide-check"
          @click="emit('use', template)"
        >
          Usar
        </Button>
      </div>
    </div>
  </div>
</template>
